import React from 'react';
import I18n from 'i18n-js';

import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';

import { core_apimessages_ShippingLabelPackage } from '../../schemas/rql';
import CustomPackageInputs from '../discovery/shipping_labels/custom_package_inputs';
import { MeasurementSystem, buildNewPackage } from '../discovery/shipping_labels/shipping_label_package';

export const CARRIER_CALC_MAX_LENGTH_INCHES = 22;
export const CARRIER_CALC_MAX_WEIGHT_POUNDS = 10;
const USPS_DIVISOR = 166;
const USPS_CUBIC_VOLUME_LOWER_LIMIT_INCHES = 1728; // the USPS-defined threshold where dimensional weight consideration begins
const CARRIER_CALC_MAX_CUBIC_VOLUME_INCHES = 1660; // the reverb-defined limit for carrier-calc eligiblity (dimensional weight of 10lb)

function PackageSizeWarning({ shippingPackage, setCarrierCalculatedMode }) {
  const { length, width, height, weight } = shippingPackage;
  if (!length || !width || !height || !weight) { return null; }
  const allInches = [length, width, height].every((dim) => dim.unit === 'in');
  const weightIsInPounds = !!weight && weight.unit === 'lb';
  if (!allInches || !weightIsInPounds) { return null; }

  const lengthInches = Number(length?.value);
  const widthInches = Number(width?.value);
  const heightInches = Number(height?.value);
  const weightPounds = Number(weight?.value);

  const allDimensionsEntered = [lengthInches, widthInches, heightInches, weightPounds].every((v) => v > 0);

  if (!allDimensionsEntered) { return null; }

  const volume_cubic_inches = lengthInches * widthInches * heightInches;

  let dimensionalWeightIfApplicable;
  if (volume_cubic_inches > USPS_CUBIC_VOLUME_LOWER_LIMIT_INCHES) {
    dimensionalWeightIfApplicable = Math.ceil(volume_cubic_inches / USPS_DIVISOR);
  }

  const exceedsActualWeightLimit = weightPounds > CARRIER_CALC_MAX_WEIGHT_POUNDS;
  const exceedsLengthLimit = [lengthInches, widthInches, heightInches].some((v) => v > CARRIER_CALC_MAX_LENGTH_INCHES);
  const exceedsDimensionalWeightLimit = !!dimensionalWeightIfApplicable && dimensionalWeightIfApplicable > CARRIER_CALC_MAX_WEIGHT_POUNDS;

  let errorTranslationKey;
  if (exceedsActualWeightLimit) {
    errorTranslationKey = 'exceedsActualWeightLimit';
  } else if (exceedsLengthLimit) {
    errorTranslationKey = 'exceedsLengthLimit';
  } else if (exceedsDimensionalWeightLimit) {
    errorTranslationKey = 'exceedsDimensionalWeightLimit';
  }

  if (errorTranslationKey) {
    return (
      <RCAlertBox data-package-warning type="error">
        <I18N
          html
          text={`discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.exceedsMaxWarnings.${errorTranslationKey}`}
          args={{
            maxLengthInches: String(CARRIER_CALC_MAX_LENGTH_INCHES),
            maxWeightPounds: String(CARRIER_CALC_MAX_WEIGHT_POUNDS),
            maxCubicInches: String(CARRIER_CALC_MAX_CUBIC_VOLUME_INCHES),
          }}
        >
          <button
            className="button-as-link"
            type="button"
            onClick={() => setCarrierCalculatedMode(false)}
          >
            {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.exceedsMaxWarnings.setCustomRate')}
          </button>
        </I18N>
      </RCAlertBox>
    );
  }

  return null;
}

interface IProps {
  shippingPackage?: core_apimessages_ShippingLabelPackage;
  setIsInCarrierCalculatedMode: (boolean) => void;
}

export default function EditShippingRateCardPackage(props: IProps) {
  const [shippingPackage, setShippingPackage] = React.useState(props.shippingPackage || buildNewPackage(MeasurementSystem.IMPERIAL));

  return (
    <>
      <CustomPackageInputs
        shippingPackage={shippingPackage}
        measurementSystem={MeasurementSystem.IMPERIAL}
        updatePackageDimensions={setShippingPackage}
        forCarrierCalculated
        hideMeasurementSystemControls
      />
      <input
        id="product_shipping_US_CON_rate_type"
        type="hidden"
        name="product[shipping][rates][][rate_type]"
        value="carrier_calculated"
      />

      <PackageSizeWarning shippingPackage={shippingPackage} setCarrierCalculatedMode={props.setIsInCarrierCalculatedMode} />
    </>
  );
}
